import { formatINR } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImageDropzone from "@/components/ImageDropzone";
import { useImageUpload } from "@/hooks/useImageUpload";
import { useAppointmentForm } from "@/hooks/useAppointmentForm";
import { Loader2 } from "lucide-react";

const EMPTY = {
  doctor_id: "", patient_name: "", patient_age: "", phone: "", appointment_date: "",
  amount: "", disease_name: "", diagnosis: "", status: "confirmed",
  prescription_path: null, prescription_filename: null,
};
const STATUSES = ["confirmed", "completed", "pending", "cancelled"];
const FIELDS = [
  { key: "patient_name", label: "Patient Name", placeholder: "Full name", testid: "appointment-patient-name-input" },
  { key: "patient_age", label: "Age", placeholder: "Age", testid: "appointment-patient-age-input", type: "number" },
  { key: "phone", label: "Patient Phone", placeholder: "+91 98765 43210", testid: "appointment-phone-input", type: "tel" },
  { key: "appointment_date", label: "Date & Time", testid: "appointment-date-picker", type: "datetime-local" },
  { key: "amount", label: "Consultation Amount (₹)", placeholder: "500", testid: "appointment-amount-input", type: "number" },
  { key: "disease_name", label: "Disease Name", placeholder: "e.g. Hypertension", testid: "appointment-disease-input" },
];

const PrescriptionPreview = ({ preview }) => {
  if (preview === "existing")
    return <div className="h-28 w-40 rounded-lg bg-teal-50 border border-teal-200 flex items-center justify-center text-sm text-teal-700">Image attached</div>;
  return preview ? <img src={preview} alt="prescription" className="h-28 w-40 object-cover rounded-lg border" /> : null;
};

export default function BookAppointmentDialog({ open, onOpenChange, doctors, onSaved, editing, prefill }) {
  const f = useAppointmentForm({ open, editing, prefill, onOpenChange, onSaved, empty: EMPTY, doctors });
  const upload = useImageUpload({ onUploaded: f.attachImage });
  const clearImage = () => { f.clearImage(); upload.reset(); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head text-2xl">{editing ? "Edit Appointment" : "New Appointment"}</DialogTitle></DialogHeader>

        <div className="grid sm:grid-cols-2 gap-4 py-2">
          <div className="space-y-2 sm:col-span-2">
            <Label>Doctor</Label>
            <Select value={f.form.doctor_id} onValueChange={f.onDoctorChange}>
              <SelectTrigger data-testid="appointment-doctor-select"><SelectValue placeholder="Select doctor" /></SelectTrigger>
              <SelectContent>
                {doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} — {d.specialty} ({formatINR(d.fee)})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {FIELDS.map(({ key, label, placeholder, testid, type = "text" }) => (
            <div key={key} className="space-y-2">
              <Label>{label}</Label>
              <Input data-testid={testid} type={type} value={f.form[key]} onChange={(e) => f.set(key, e.target.value)} placeholder={placeholder} />
            </div>
          ))}

          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={f.form.status} onValueChange={(v) => f.set("status", v)}>
              <SelectTrigger data-testid="appointment-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label>Diagnosis Comments</Label>
            <Textarea data-testid="appointment-diagnosis-input" value={f.form.diagnosis} onChange={(e) => f.set("diagnosis", e.target.value)} rows={3} placeholder="Clinical notes, advice, follow-up…" />
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label>Prescription Image</Label>
            <ImageDropzone upload={upload} testid="appointment-prescription-upload" label="Click to upload prescription photo/scan"
              preview={f.preview ? <PrescriptionPreview preview={f.preview} /> : null} onClear={clearImage} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="appointment-form-submit-button" onClick={f.submit} disabled={f.saving} className="bg-teal-600 hover:bg-teal-700 text-white">
            {f.saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {!f.saving && (editing ? "Save Changes" : "Book Appointment")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
